import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  Filter,
  Download,
  List,
  Grid,
  BarChart3,
  Zap,
  AlertCircle,
  CheckCircle,
  MinusCircle
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { reportsAPI, roomsAPI } from '../services/api';
import { formatCurrency, getMonthName } from '../utils/helpers';
import toast from 'react-hot-toast';

const ElectricityTrendsPage = () => {
  const [trendsData, setTrendsData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({
    period: '12',
    from_month: '',
    from_year: '',
    to_month: '',
    to_year: ''
  });

  useEffect(() => {
    fetchTrendsData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchTrendsData();
    }
  }, [filters.period]);

  const fetchTrendsData = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (filters.period) params.period = filters.period;
      if (filters.from_month && filters.from_year && filters.to_month && filters.to_year) {
        params.from_month = filters.from_month;
        params.from_year = filters.from_year;
        params.to_month = filters.to_month;
        params.to_year = filters.to_year;
      }

      const response = await reportsAPI.getElectricityTrends(params);
      
      setTrendsData(response.data.trends);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching trends data:', error);
      toast.error('Failed to load electricity trends');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyCustomRange = () => {
    if (filters.from_month && filters.from_year && filters.to_month && filters.to_year) {
      fetchTrendsData();
    } else {
      toast.error('Please select both from and to dates');
    }
  };

  const clearFilters = () => {
    setFilters({
      period: '12',
      from_month: '',
      from_year: '',
      to_month: '',
      to_year: ''
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'deficit':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'covered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'surplus':
        return <CheckCircle className="w-5 h-5 text-lime-500" />;
      default:
        return <MinusCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      deficit: 'bg-red-100 text-red-800 border-red-200',
      covered: 'bg-green-100 text-green-800 border-green-200',
      surplus: 'bg-lime-100 text-lime-800 border-lime-200'
    };
    return badges[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatPDFDate = (dateString) => {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const dayName = days[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${dayName} ${day}-${month}-${year}`;
  };

  const handleDownloadPDF = () => {
    if (trendsData.length === 0) {
      toast.error('No data to download');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Electricity Trends Report</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            background: white;
            color: #000;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #3b82f6;
            padding-bottom: 15px;
          }
          .header h1 {
            font-size: 28px;
            margin-bottom: 8px;
            color: #1e40af;
          }
          .header p {
            font-size: 13px;
            color: #555;
          }
          .summary {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin-bottom: 25px;
            padding: 15px;
            background: #eff6ff;
            border-radius: 6px;
            border: 1px solid #bfdbfe;
          }
          .summary-item {
            text-align: center;
          }
          .summary-label {
            font-size: 11px;
            color: #1e40af;
            margin-bottom: 5px;
            font-weight: 600;
          }
          .summary-value {
            font-size: 18px;
            font-weight: bold;
            color: #1e3a8a;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 11px;
          }
          thead {
            background: #1e40af;
            color: white;
          }
          th {
            padding: 10px 6px;
            text-align: left;
            font-weight: 600;
            font-size: 11px;
          }
          th.center {
            text-align: center;
          }
          td {
            padding: 8px 6px;
            border-bottom: 1px solid #e0e0e0;
          }
          td.center {
            text-align: center;
          }
          tbody tr:nth-child(even) {
            background: #f9fafb;
          }
          tbody tr:hover {
            background: #eff6ff;
          }
          .amount {
            font-weight: 600;
            text-align: center;
          }
          .badge {
            display: inline-block;
            padding: 3px 8px;
            border-radius: 3px;
            font-size: 10px;
            font-weight: 600;
          }
          .badge-deficit {
            background: #fee2e2;
            color: #991b1b;
            border: 1px solid #fecaca;
          }
          .badge-covered {
            background: #dcfce7;
            color: #14532d;
            border: 1px solid #bbf7d0;
          }
          .badge-surplus {
            background: #ecfccb;
            color: #3f6212;
            border: 1px solid #d9f99d;
          }
          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 10px;
            color: #666;
          }
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 24px;
            background: #1e40af;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 600;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .print-button:hover {
            background: #1e3a8a;
          }
          @media print {
            .print-button {
              display: none;
            }
            body {
              padding: 0;
            }
            @page {
              size: A4 landscape;
              margin: 15mm;
            }
          }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">Print / Save as PDF</button>
        
        <div class="header">
          <h1>⚡ ELECTRICITY TRENDS ANALYSIS</h1>
          <p>Bill Coverage & Owner Contribution Report</p>
          <p style="font-size: 11px; margin-top: 5px;">Generated on ${formatPDFDate(new Date().toISOString())}</p>
        </div>

        ${summary ? `
        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">Total Bill Amount</div>
            <div class="summary-value">${formatCurrency(summary.total_bill_amount)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Room Contributions</div>
            <div class="summary-value">${formatCurrency(summary.total_room_contributions || 0)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Owner Contribution</div>
            <div class="summary-value">${formatCurrency(summary.total_owner_contribution || 0)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Average Variance</div>
            <div class="summary-value">${(summary.avg_variance_percentage || 0).toFixed(1)}%</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Months with Deficit</div>
            <div class="summary-value">${summary.months_with_deficit || 0}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Months Fully Covered</div>
            <div class="summary-value">${summary.months_fully_covered || 0}</div>
          </div>
        </div>
        ` : ''}

        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th class="center">Total Bill</th>
              <th class="center">Room Contributions</th>
              <th class="center">Owner Contribution</th>
              <th class="center">Variance</th>
              <th class="center">Variance %</th>
              <th class="center">Main (kWh)</th>
              <th class="center">Room (kWh)</th>
              <th class="center">Variance (kWh)</th>
              <th class="center">Status</th>
            </tr>
          </thead>
          <tbody>
            ${trendsData.map(trend => {
              const unitsVariance = trend.units_variance || 0;
              const unitsVarianceColor = unitsVariance > 0 ? '#dc2626' : unitsVariance < 0 ? '#eab308' : '#000';
              
              return `
                <tr>
                  <td><strong>${trend.month || 'N/A'}</strong></td>
                  <td class="amount">${formatCurrency(trend.total_bill || 0)}</td>
                  <td class="amount">${formatCurrency(trend.room_contributions || 0)}</td>
                  <td class="amount" style="color: ${(trend.owner_contribution || 0) > 0 ? '#dc2626' : '#16a34a'}">
                    ${formatCurrency(trend.owner_contribution || 0)}
                  </td>
                  <td class="amount">${formatCurrency(trend.variance || 0)}</td>
                  <td class="center">${(trend.variance_percentage || 0).toFixed(1)}%</td>
                  <td class="center"><strong>${(trend.total_units || 0).toFixed(0)}</strong></td>
                  <td class="center">${(trend.room_units || 0).toFixed(0)}</td>
                  <td class="center" style="color: ${unitsVarianceColor}"><strong>${unitsVariance.toFixed(0)}</strong></td>
                  <td class="center">
                    <span class="badge badge-${trend.status || 'covered'}">
                      ${trend.status ? trend.status.charAt(0).toUpperCase() + trend.status.slice(1) : 'Unknown'}
                    </span>
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Electricity Trends Report - Tenant Bills Management System</p>
          <p>💡 Deficit = Owner pays additional | Covered = Fully paid by tenants | Surplus = Excess coverage</p>
        </div>
      </body>
      </html>
    `;

    const newWindow = window.open('', '_blank');
    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  if (loading && trendsData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Prepare chart data for owner contribution
  const ownerContributionData = trendsData.map(trend => ({
    month: trend.month,
    contribution: trend.owner_contribution,
    status: trend.status
  }));

  // Prepare comparison chart data
  const comparisonData = trendsData.map(trend => ({
    month: trend.month,
    'Total Bill': trend.total_bill,
    'Room Contributions': trend.room_contributions,
    'Owner Contribution': trend.owner_contribution
  }));

  // Prepare variance trend data
  const varianceData = trendsData.map(trend => ({
    month: trend.month,
    variance: trend.variance_percentage
  }));

  // Calculate distribution
  const totalBill = summary?.total_bill_amount || 0;
  const totalRooms = summary?.total_room_contributions || 0;
  const totalOwner = summary?.total_owner_contribution || 0;

  const distributionData = [
    { name: 'Room Contributions', value: totalRooms, color: '#10b981' },
    { name: 'Owner Contribution', value: totalOwner > 0 ? totalOwner : 0, color: '#ef4444' }
  ].filter(item => item.value > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
            Electricity Trends Analysis
          </h1>
          <p className="text-secondary-600 dark:text-secondary-300">
            Compare electricity bills vs room contributions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownloadPDF}
            className="btn-outline flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
          <div className="flex items-center gap-1 bg-secondary-100 dark:bg-secondary-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-2 rounded transition-all ${
                viewMode === 'list' 
                  ? 'bg-white dark:bg-secondary-700 shadow-sm text-primary-600 dark:text-primary-400' 
                  : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-100'
              }`}
              title="List View"
            >
              <List className="w-4 h-4" />
              <span className="text-sm font-medium">List</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-2 px-3 py-2 rounded transition-all ${
                viewMode === 'grid' 
                  ? 'bg-white dark:bg-secondary-700 shadow-sm text-primary-600 dark:text-primary-400' 
                  : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-100'
              }`}
              title="Grid View"
            >
              <Grid className="w-4 h-4" />
              <span className="text-sm font-medium">Grid</span>
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`flex items-center gap-2 px-3 py-2 rounded transition-all ${
                viewMode === 'chart' 
                  ? 'bg-white dark:bg-secondary-700 shadow-sm text-primary-600 dark:text-primary-400' 
                  : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-100'
              }`}
              title="Chart View"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm font-medium">Charts</span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-700 to-cyan-900 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Months</p>
                <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                  {summary?.total_months || 0}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-700 to-cyan-900 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Total Bills</p>
                <p className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                  {formatCurrency(summary?.total_bill_amount || 0)}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Room Contrib.</p>
                <p className="text-lg font-bold text-emerald-600">
                  {formatCurrency(summary?.total_room_contributions || 0)}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${(summary?.total_owner_contribution || 0) > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-lime-500 to-green-500'}`}>
                {(summary?.total_owner_contribution || 0) > 0 ? (
                  <AlertCircle className="w-5 h-5 text-white" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Owner Contrib.</p>
                <p className={`text-lg font-bold ${(summary?.total_owner_contribution || 0) > 0 ? 'text-red-600' : 'text-lime-600'}`}>
                  {formatCurrency(summary?.total_owner_contribution || 0)}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                <TrendingDown className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Avg Variance</p>
                <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                  {summary?.avg_variance_percentage?.toFixed(1) || '0.0'}%
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4"
          >
            <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-3">Coverage</p>
            <div className="flex items-center justify-around">
              <div className="text-center">
                <p className="text-xl font-bold text-red-600">{summary?.months_with_deficit || 0}</p>
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Deficit</span>
              </div>
              <div className="w-px h-8 bg-secondary-200 dark:bg-secondary-600" />
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-600">{summary?.months_fully_covered || 0}</p>
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Covered</span>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-secondary-400 dark:text-secondary-300" />
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 flex-1">
            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-1">
                Period
              </label>
              <select
                name="period"
                value={filters.period}
                onChange={handleFilterChange}
                className="form-input text-sm"
              >
                <option value="6">Last 6 Months</option>
                <option value="12">Last 12 Months</option>
                <option value="24">Last 24 Months</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-1">
                From Month
              </label>
              <select
                name="from_month"
                value={filters.from_month}
                onChange={handleFilterChange}
                className="form-input text-sm"
              >
                <option value="">Select Month</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {getMonthName(i + 1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-1">
                From Year
              </label>
              <select
                name="from_year"
                value={filters.from_year}
                onChange={handleFilterChange}
                className="form-input text-sm"
              >
                <option value="">Select Year</option>
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-1">
                To Month
              </label>
              <select
                name="to_month"
                value={filters.to_month}
                onChange={handleFilterChange}
                className="form-input text-sm"
              >
                <option value="">Select Month</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {getMonthName(i + 1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-1">
                To Year
              </label>
              <select
                name="to_year"
                value={filters.to_year}
                onChange={handleFilterChange}
                className="form-input text-sm"
              >
                <option value="">Select Year</option>
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>

            <div className="flex items-end gap-2">
              {filters.from_month && filters.from_year && filters.to_month && filters.to_year && (
                <button
                  onClick={handleApplyCustomRange}
                  className="btn-primary text-sm px-4 py-2 flex-1"
                >
                  Apply Range
                </button>
              )}
              <button
                onClick={clearFilters}
                className="btn-outline text-sm px-4 py-2 flex-1"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Display */}
      {trendsData.length > 0 ? (
        <>
          {/* List View */}
          {viewMode === 'list' && (
            <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-teal-700 to-cyan-900 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">Electricity Trends</h3>
                    <p className="text-teal-100 text-xs">{trendsData.length} month{trendsData.length !== 1 ? 's' : ''} of data</p>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary-50 dark:bg-secondary-700/50 border-b border-secondary-200 dark:border-secondary-600">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Month</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Total Bill</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Room Contrib.</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Owner Contrib.</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Variance</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Var %</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Main kWh</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Room kWh</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">kWh Var</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
                    {trendsData.map((trend, index) => {
                      const unitsVariance = (trend.units_variance || 0);
                      const unitsVarianceColor = unitsVariance > 0 ? 'text-red-600' : unitsVariance < 0 ? 'text-yellow-600' : 'text-secondary-500';

                      return (
                        <motion.tr
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="hover:bg-secondary-50 dark:hover:bg-secondary-700/30 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-700 to-cyan-900 flex items-center justify-center flex-shrink-0">
                                <Zap className="w-4 h-4 text-white" />
                              </div>
                              <span className="font-semibold text-secondary-900 dark:text-secondary-100 text-sm">{trend.month}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-teal-700 dark:text-teal-400 text-sm">
                            {formatCurrency(trend.total_bill)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-emerald-600 text-sm">
                            {formatCurrency(trend.room_contributions || 0)}
                          </td>
                          <td className={`px-4 py-3 text-right font-semibold text-sm ${(trend.owner_contribution || 0) > 0 ? 'text-red-600' : 'text-lime-600'}`}>
                            {formatCurrency(trend.owner_contribution || 0)}
                          </td>
                          <td className="px-4 py-3 text-right text-secondary-700 dark:text-secondary-300 text-sm">
                            {formatCurrency(trend.variance || 0)}
                          </td>
                          <td className="px-4 py-3 text-center text-secondary-700 dark:text-secondary-300 text-sm">
                            {(trend.variance_percentage || 0).toFixed(1)}%
                          </td>
                          <td className="px-4 py-3 text-center font-semibold text-secondary-900 dark:text-secondary-100 text-sm">
                            {(trend.total_units || 0).toFixed(0)}
                          </td>
                          <td className="px-4 py-3 text-center text-secondary-700 dark:text-secondary-300 text-sm">
                            {(trend.room_units || 0).toFixed(0)}
                          </td>
                          <td className={`px-4 py-3 text-center font-semibold text-sm ${unitsVarianceColor}`}>
                            {unitsVariance.toFixed(0)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              {getStatusIcon(trend.status)}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                trend.status === 'deficit' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                trend.status === 'surplus' ? 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400' :
                                'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              }`}>
                                {trend.status?.charAt(0).toUpperCase() + trend.status?.slice(1) || 'Unknown'}
                              </span>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendsData.map((trend, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className={`px-5 py-4 bg-gradient-to-r ${
                    trend.status === 'deficit' ? 'from-red-500 to-red-700' :
                    trend.status === 'surplus' ? 'from-lime-500 to-green-600' :
                    'from-teal-700 to-cyan-900'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                          {trend.status === 'deficit' ? (
                            <TrendingDown className="w-5 h-5 text-white" />
                          ) : (
                            <TrendingUp className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-base">{trend.month}</h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold mt-0.5 ${
                            trend.status === 'deficit' ? 'bg-white/20 text-white' :
                            trend.status === 'surplus' ? 'bg-white/20 text-white' :
                            'bg-white/20 text-white'
                          }`}>
                            {getStatusIcon(trend.status)}
                            {trend.status?.charAt(0).toUpperCase() + trend.status?.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white/70 text-xs">Total Bill</p>
                        <p className="text-white font-bold text-lg">{formatCurrency(trend.total_bill)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-3">
                    {/* Contributions */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3">
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-1">Room Contrib.</p>
                        <p className="text-base font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(trend.room_contributions || 0)}</p>
                      </div>
                      <div className={`rounded-xl p-3 ${(trend.owner_contribution || 0) > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-lime-50 dark:bg-lime-900/20'}`}>
                        <p className={`text-xs font-medium mb-1 ${(trend.owner_contribution || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-lime-600 dark:text-lime-400'}`}>Owner Contrib.</p>
                        <p className={`text-base font-bold ${(trend.owner_contribution || 0) > 0 ? 'text-red-700 dark:text-red-300' : 'text-lime-700 dark:text-lime-300'}`}>{formatCurrency(trend.owner_contribution || 0)}</p>
                      </div>
                    </div>

                    {/* Variance row */}
                    <div className="flex items-center justify-between py-2 border-t border-secondary-100 dark:border-secondary-700">
                      <span className="text-xs text-secondary-500 dark:text-secondary-400">Variance</span>
                      <span className="text-sm font-semibold text-secondary-700 dark:text-secondary-300">
                        {formatCurrency(trend.variance || 0)} <span className="text-secondary-400">({(trend.variance_percentage || 0).toFixed(1)}%)</span>
                      </span>
                    </div>

                    {/* kWh readings */}
                    <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-3 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-secondary-500 dark:text-secondary-400">Main meter</span>
                        <span className="font-semibold text-secondary-800 dark:text-secondary-200">{(trend.total_units || 0).toFixed(0)} kWh</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-secondary-500 dark:text-secondary-400">Room meters</span>
                        <span className="font-semibold text-secondary-800 dark:text-secondary-200">{(trend.room_units || 0).toFixed(0)} kWh</span>
                      </div>
                      <div className="flex items-center justify-between text-xs border-t border-secondary-200 dark:border-secondary-600 pt-2">
                        <span className="text-secondary-500 dark:text-secondary-400">kWh variance</span>
                        <span className={`font-semibold ${(trend.units_variance || 0) > 0 ? 'text-red-600' : (trend.units_variance || 0) < 0 ? 'text-yellow-600' : 'text-secondary-500'}`}>
                          {(trend.units_variance || 0).toFixed(0)} kWh
                        </span>
                      </div>
                    </div>

                    {/* Coverage bar */}
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-secondary-500 dark:text-secondary-400">Coverage</span>
                        <span className="font-medium text-secondary-700 dark:text-secondary-300">{(trend.variance_percentage || 0).toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-secondary-200 dark:bg-secondary-600 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${
                            trend.status === 'deficit' ? 'bg-red-500' :
                            trend.status === 'surplus' ? 'bg-lime-500' :
                            'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(100, Math.abs(trend.variance_percentage || 0))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Chart View */}
          {viewMode === 'chart' && (
            <div className="space-y-6">
              {/* KPI cards */}
              {summary && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-700 to-cyan-900 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Total Billed</p>
                      <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">{formatCurrency(summary?.total_bill_amount || 0)}</p>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Room Contributions</p>
                      <p className="text-xl font-bold text-emerald-600">{formatCurrency(summary?.total_room_contributions || 0)}</p>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${(summary?.total_owner_contribution || 0) > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-lime-500 to-green-500'}`}>
                      {(summary?.total_owner_contribution || 0) > 0 ? <AlertCircle className="w-6 h-6 text-white" /> : <CheckCircle className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Owner Contribution</p>
                      <p className={`text-xl font-bold ${(summary?.total_owner_contribution || 0) > 0 ? 'text-red-600' : 'text-lime-600'}`}>{formatCurrency(summary?.total_owner_contribution || 0)}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Comparison Chart */}
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-teal-700 to-cyan-900 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <BarChart3 className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-sm">Bill vs Contributions Comparison</h3>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={comparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          tickFormatter={(value) => `$${value}`}
                        />
                        <Tooltip
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Bar dataKey="Total Bill" fill="#0f766e" />
                        <Bar dataKey="Room Contributions" fill="#10b981" />
                        <Line
                          type="monotone"
                          dataKey="Owner Contribution"
                          stroke="#ef4444"
                          strokeWidth={2}
                          dot={{ fill: '#ef4444', r: 4 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Owner Contribution Chart */}
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-teal-700 to-cyan-900 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <TrendingDown className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-sm">Owner Contribution Trend</h3>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={ownerContributionData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          tickFormatter={(value) => `$${value}`}
                        />
                        <Tooltip
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Bar dataKey="contribution" name="Owner Contribution">
                          {ownerContributionData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.contribution > 0 ? '#ef4444' : '#84cc16'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Variance Trend */}
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-teal-700 to-cyan-900 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-sm">Variance Percentage Trend</h3>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={varianceData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                          tickFormatter={(value) => `${value}%`}
                        />
                        <Tooltip
                          formatter={(value) => `${(value || 0).toFixed(1)}%`}
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="variance"
                          stroke="#0f766e"
                          strokeWidth={3}
                          dot={{ fill: '#0f766e', r: 5 }}
                          name="Variance %"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Distribution Pie Chart */}
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-teal-700 to-cyan-900 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-sm">Overall Contribution Distribution</h3>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={distributionData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {distributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700">
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-700 to-cyan-900 flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <p className="text-secondary-700 dark:text-secondary-200 text-lg font-semibold">
              No electricity trends data available
            </p>
            <p className="text-sm text-secondary-400 dark:text-secondary-400 mt-2">
              Data will appear once electricity bills and meter readings are recorded
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElectricityTrendsPage;
